/**
 * Editor-side types.
 *
 * The node/tree/version shapes ALREADY live in `@/data/services` — we import
 * and re-export them here so there is exactly ONE node type across studio-ui.
 * Do NOT redefine ComponentNode / ComponentTree / PageVersion locally.
 *
 * What this file owns are the AI-proposal + op types (ported from builder-ui),
 * which are not part of the data-service layer.
 */

import type { ComponentNode, ComponentTree, PageVersion } from "@/data/services";

export type { ComponentNode, ComponentTree, PageVersion };

// ---------- AI proposals ----------

/**
 * Discriminated union of editor ops. The legacy shape (no `op` field, just
 * `node_id` + `prop` + `value`) is treated as `set_prop` for backward
 * compatibility — older agent outputs and the existing experiment payloads
 * still use that shape.
 */
export type SetPropOp = {
  op?: "set_prop";
  node_id: string;
  prop: string;
  value: unknown;
};

export type AddNodeOp = {
  op: "add_node";
  /** Existing node to insert into. Must accept children. */
  parent_node_id: string;
  /** Component name from the registry (e.g. "Hero", "Stats"). */
  component: string;
  /** Initial props for the new node. */
  props?: Record<string, unknown>;
  /** Optional client-assigned id; if absent, the editor generates one. */
  new_node_id?: string;
};

export type RemoveNodeOp = {
  op: "remove_node";
  node_id: string;
};

export type MoveNodeOp = {
  op: "move_node";
  node_id: string;
  /** Sibling shift: -1 up, +1 down. Used when `to_parent_id` is absent. */
  delta?: -1 | 1;
  /** Re-parent: move the node to be a child of this parent (must accept children). */
  to_parent_id?: string;
  /** Insertion index within the new parent (default: append). Only with to_parent_id. */
  index?: number;
};

export type ApplyRecipeOp = {
  op: "apply_recipe";
  /** A layout recipe id from @dynamic-landing/layout
   *  (bento | editorial | feature-grid | brutalist | stack). Restructures the
   *  WHOLE page — wraps blocks into a grid (or unwraps to a stack). */
  recipe_id: string;
};

export type WrapNodesOp = {
  op: "wrap_nodes";
  /** Existing sibling node ids to GROUP (must share one parent). */
  node_ids: string[];
  /** Container component to wrap them in (must accept children, e.g. Grid/Columns/Container). */
  component: string;
  /** Initial props for the new container (e.g. {columns:"2"}). */
  props?: Record<string, unknown>;
  /** Optional client-assigned id for the new container. */
  new_node_id?: string;
};

export type UnwrapNodeOp = {
  op: "unwrap_node";
  /** A container node to UNGROUP — its children are promoted into its parent. */
  node_id: string;
};

export type ProposalOp =
  | SetPropOp
  | AddNodeOp
  | RemoveNodeOp
  | MoveNodeOp
  | ApplyRecipeOp
  | WrapNodesOp
  | UnwrapNodeOp;

export type Proposal = {
  id: string;
  kind: "tree_patch" | "new_component" | "experiment";
  rationale: string;
  ops?: ProposalOp[];
};

export function opKind(
  op: ProposalOp,
):
  | "set_prop"
  | "add_node"
  | "remove_node"
  | "move_node"
  | "apply_recipe"
  | "wrap_nodes"
  | "unwrap_node" {
  if ("op" in op && op.op) return op.op;
  return "set_prop";
}
